import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import channelPermissions from '../../../utils/channelPermissions';

import { routes } from '../../../config';
import i18n from '../../../i18n';
import { AuthReducerState, Channel, RootState } from '../../../interfaces';
import './styles.scss';
import { getChannelLogoUrl, getOngoingProgramName } from '../../../utils/tvUtils';
import styles from './styles.module.scss';
import { usePlaybills } from '../../../queries/playbills/queries';

interface WatchTvLinkProps {
  children: JSX.Element | null;
  pathname: string;
}

const WatchTvLink = ({ children, pathname }: WatchTvLinkProps) => {
  const location = useLocation<{ prevPath: string | undefined }>();
  return (
    <Link
      className={styles.channelLink}
      draggable="false"
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      to={{
        pathname: pathname,
        state: { prevPath: location.pathname },
      }}
    >
      {children}
    </Link>
  );
};
interface Props {
  channel: Channel;
  isProgramArchive?: boolean | undefined;
  authReducer: AuthReducerState;
}

function ChannelsBannerListItem({ channel, authReducer, isProgramArchive }: Props) {
  const { data: playbills, isLoading } = usePlaybills({ channelIds: [channel.contentId], date: new Date() });

  if (isLoading) {
    return <ChannelsBannerListItem.Skeleton index={Number(channel.chanNo)} />;
  }

  if (!playbills) {
    return <ChannelsBannerListItem.Skeleton index={Number(channel.chanNo)} />;
  }
  const ongoingProgramName = getOngoingProgramName(playbills);
  const isLocked = () => {
    const permission = channelPermissions(channel, authReducer);
    let locked = false;

    if (isProgramArchive) {
      if (channel.channelPermissions.onlyInsideForCatchup && !authReducer.isHome) {
        locked = true;
      }
    }

    return permission.locked || locked;
  };

  const overlayClass = (isLocked() ? 'channelMessage' : '') + (isProgramArchive ? ' programarchive' : '');

  const listItemClass = () => {
    if (isLocked()) {
      return 'channel locked';
    }

    return 'channel';
  };

  const LockIcon = () => {
    return isLocked() ? (
      <span>
        <div className="lock aib-icon">p</div>
      </span>
    ) : null;
  };

  const TopRightIcons = () => (
    <div className="top-right-icons">
      <LockIcon />
    </div>
  );

  const channelBannerLink = isProgramArchive
    ? routes.unmanaged.base + routes.programarchive.base + routes.programarchive.channel + '/' + channel.contentId
    : routes.unmanaged.base + routes.tv.base + '/' + channel.contentId;

  const getPermissionString = () => {
    if (authReducer.isGuest) {
      return null;
    }
    const { geoLocked, outsideHome, notInSubscription } = channelPermissions(channel, authReducer);

    if (notInSubscription) {
      return i18n.t('permission not in subscription');
    }

    if (geoLocked) {
      return i18n.t('geolocked_playback');
    }

    if (outsideHome) {
      return i18n.t("can't show outside home");
    }
  };

  const PermissionOverlay = () => {
    const permissionString = getPermissionString();

    if (!permissionString) {
      return null;
    }

    return (
      <div className={overlayClass}>
        <span className={'no-sub'}>{permissionString}</span>
      </div>
    );
  };

  return (
    <div key={channel.chanNo + channel.name} className={listItemClass()}>
      <div className={styles.channelContainer} key={channel.chanNo}>
        <WatchTvLink pathname={channelBannerLink}>
          <>
            <PermissionOverlay />
            <TopRightIcons />
            <div className={'image-container'}>
              <img
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                src={getChannelLogoUrl(channel)}
                alt={channel.name}
                title={channel.name}
              />
            </div>
            {ongoingProgramName && !isProgramArchive && (
              <div className="title" title={ongoingProgramName}>
                {ongoingProgramName}
              </div>
            )}
          </>
        </WatchTvLink>
      </div>
    </div>
  );
}

ChannelsBannerListItem.Skeleton = ({ index }: { index: number }) => {
  return (
    <div key={index} className={'channel'}>
      <div className={styles.channelContainer}></div>
    </div>
  );
};
export default connect((state: RootState) => ({
  authReducer: state.authReducer,
}))(ChannelsBannerListItem);
